import React, {useEffect, useState} from 'react';
import {HexColorPicker} from 'react-colorful';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Icon} from '@iconify/react';
import {usePresentationThemeDetailQuery} from '../../../hooks/usePresentationThemeDetailQuery';
import {isValidHexColor} from '../../util/hexColor';
import Dropdown, {DropdownAlignment} from '../../../incase-domain/components/dropdown/Dropdown';
import FavoriteColorsList from './FavoriteColorsList';
import {selectedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import ResetIcon from '../../../../assets/icons/ResetIcon.svg?react';
import TooltipWrapper from './TooltipWrapper';

interface ColorPickerProps {
    color?: string | null;
    onChange: (color: string | null | undefined) => void;
    secondary?: boolean;
    align?: DropdownAlignment;
    text?: string;
    forPresentation?: boolean;
    textPosition?: boolean;
    menuPositionOverridden?: boolean;
    removeSlideBackgroundColor?: boolean;
    resetThemeSlideBackground?: boolean;
}

function ColorPicker(props: ColorPickerProps): JSX.Element {
    const presentation = useSelector(selectedPresentationSelector);
    const {data: presentationTheme} = usePresentationThemeDetailQuery(presentation.presentationThemeId);

    const {
        color = '#040138',
        onChange,
        secondary = false,
        align = 'right',
        text = '',
        forPresentation = true,
        textPosition = false,
        menuPositionOverridden = false,
        resetThemeSlideBackground,
        removeSlideBackgroundColor,
    } = props;

    const {t} = useTranslation();
    const [colorPicker, setColorPicker] = useState<string | null>(color);
    const [isColorReseted, setIsColorReseted] = useState(false);

    useEffect(() => {
        setColorPicker(color);
    }, [color]);

    function changeHandler(color: string | null): void {
        /* TODO show indication if it is invalid */
        if (color) {
            if (isValidHexColor(color)) {
                onChange(color);
                setColorPicker(color);
            }
        } else {
            onChange(null);
            setColorPicker(null);
        }
    }

    function getDropdownButton(): JSX.Element {
        if (secondary) {
            return (
                <div className="u-flex u-v-center">
                    {textPosition ? (
                        <div className="u-flex u-h-between" style={{width: '100%'}}>
                            {text && <div>{text}</div>}
                            <div className="c-colorPicker__color" style={{backgroundColor: color || ''}} />
                        </div>
                    ) : (
                        <>
                            <div className="c-colorPicker__color" style={{backgroundColor: color || ''}} />
                            {text && <div className="u-ml-1">{text}</div>}
                        </>
                    )}
                </div>
            );
        }

        return (
            <div className={`c-colorPicker__edit ${color && !colors.includes(color) && !!colorPicker && 'is-edit-color'}`}>
                <div className="c-colorPicker__color">
                    <Icon icon="fa-solid:brush" />
                </div>
                {color !== '' && <div className="c-colorPicker__edit_color">{color}</div>}
            </div>
        );
    }

    return (
        <div className="c-colorPicker" style={menuPositionOverridden ? {display: 'block', width: '100%'} : {display: 'flex'}}>
            {!secondary &&
                colors.map((item, index) => (
                    <div
                        key={`c-colorPicker-${index}`}
                        className={`c-colorPicker__color ${item === color ? 'is-active' : '#ffffff'}`}
                        onClick={() => changeHandler(item)}
                        style={{backgroundColor: item}}
                    />
                ))}

            <Dropdown
                align={align}
                modifier="c-dropdown--fit-content"
                hasButtons
                onSave={() => {
                    changeHandler(colorPicker);
                }}
                onCancel={() => {
                    if (color !== colorPicker) {
                        setColorPicker(color);
                    }
                }}
                button={getDropdownButton()}
                saveLabel={t('common.save')}
                cancelLabel={t('common.cancel')}
                menuPositionOverridden={menuPositionOverridden}
                isColorReseted={isColorReseted}
                setIsColorReseted={setIsColorReseted}
            >
                <HexColorPicker color={colorPicker || undefined} onChange={setColorPicker} />

                <div className="u-mt-2 u-flex u-v-center">
                    <input
                        className="e-input"
                        type="text"
                        value={colorPicker || undefined}
                        onChange={(e) => setColorPicker(e.target.value)}
                    />
                    {removeSlideBackgroundColor && (
                        <TooltipWrapper id="reset-slide-color" content={<>{t('presEditPage.subNavBar.resetBackgroundColor')}</>}>
                            <div className="u-flex u-v-center">
                                <button
                                    type="button"
                                    className="u-ml-1"
                                    onClick={() => {
                                        changeHandler(null);
                                        setIsColorReseted(true);
                                    }}
                                >
                                    <ResetIcon />
                                </button>
                            </div>
                        </TooltipWrapper>
                    )}
                </div>
                {forPresentation && presentationTheme?.favoriteColors && presentationTheme.favoriteColors.length > 0 && (
                    <FavoriteColorsList colorToSelect={colorPicker} setColorToSelect={setColorPicker} />
                )}
            </Dropdown>
            {resetThemeSlideBackground && (
                <TooltipWrapper id="remove-background-color" content={<>{t('presEditPage.settings.background.colorReset')}</>}>
                    <button
                        type="button"
                        className="c-colorPicker__color u-ml-1"
                        style={{border: 'none', marginRight: '5px'}}
                        onClick={() => {
                            changeHandler(null);
                        }}
                    >
                        <ResetIcon />
                    </button>
                </TooltipWrapper>
            )}
        </div>
    );
}

// initial color circles for user choosing (primary colors)
const colors = ['#040138', '#5611A5', '#D83BCF', '#0B945A', '#186ED2', '#FF2E2E', '#FDA014', '#43416D', '#222222'];

export default ColorPicker;
